import {
  GenericEntityType,
  GenericProviderType,
  Provider,
  ProviderFilters,
  TemplateEntity,
  WbSafeNunjucksApi,
} from '@agilelab/plugin-wb-platform';
import { Environment } from '@agilelab/plugin-wb-platform-common';
import { WitboostSearchApi } from '@agilelab/plugin-wb-search';
import { CatalogApi } from '@backstage/catalog-client';
import { createContext, Provider as ReactProvider, useContext } from 'react';
import { ConsumablesHandlerProvider } from './ConsumableProvider/ConsumablesProvider';
import { DataContractHandlerProvider } from './DataContractProvider/DataContractProvider';
import { DomainHandlerProvider } from './DomainProvider/DomainProvider';
import { ResourceHandlerProvider } from './ResourceProvider/ResourceProvider';
import { SystemHandlerProvider } from './SystemProvider/SystemProvider';
import { RemoteHandlerProvider } from './RemoteProvider/RemoteProvider';

export enum AvailableEntities {
  SYSTEM = 'System',
  DOMAIN = 'Domain',
  RESOURCE = 'Resource',
  CONSUMABLE = 'Consumable',
  DATA_CONTRACT = 'DataContract',
  REMOTE = 'Remote',
}

export const entityHandler = (
  searchApi: WitboostSearchApi,
  catalogApi: CatalogApi,
  nunjucksApi: WbSafeNunjucksApi,
  availableKinds: TemplateEntity[],
  environments: Environment[],
  priorityEnv: Environment,
  formContext: any,
) =>
  new Map<string, Provider<GenericEntityType[], ProviderFilters>>([
    [
      AvailableEntities.SYSTEM,
      new SystemHandlerProvider(nunjucksApi, catalogApi, availableKinds),
    ],
    [
      AvailableEntities.DOMAIN,
      new DomainHandlerProvider(nunjucksApi, catalogApi, availableKinds),
    ],
    [
      AvailableEntities.RESOURCE,
      new ResourceHandlerProvider(nunjucksApi, catalogApi, availableKinds),
    ],
    [
      AvailableEntities.CONSUMABLE,
      new ConsumablesHandlerProvider(
        nunjucksApi,
        searchApi,
        availableKinds,
        environments,
        priorityEnv,
      ),
    ],
    [
      AvailableEntities.DATA_CONTRACT,
      new DataContractHandlerProvider(
        nunjucksApi,
        formContext,
        catalogApi,
        availableKinds,
      ),
    ],
    [
      AvailableEntities.REMOTE,
      new RemoteHandlerProvider(nunjucksApi, availableKinds),
    ],
  ]);

export function contextProviderFactory<
  T extends GenericProviderType<GenericEntityType[], ProviderFilters>,
>(): [() => T, ReactProvider<T | undefined>] {
  const context = createContext<T | undefined>(undefined);

  const useProvider = () => {
    const c = useContext(context);

    if (c === undefined) {
      throw new Error(
        'The hook "useProvider" must be used within an existing context.',
      );
    }

    return c;
  };

  return [useProvider, context.Provider];
}
